import { z } from 'zod';
import { IdempotencyKeySchema, IsoDateTimeSchema, UlidSchema, UuidSchema } from '../primitives.js';

/**
 * Chat message. Stored in DynamoDB keyed by chatId. Server assigns
 * monotonic per-chat `sequenceNumber` at receive time; clients render by
 * (sequenceNumber, createdAt) to ensure deterministic ordering even with
 * simultaneous sends.
 */
export const MessageSchema = z.object({
  id: UlidSchema,
  chatId: UuidSchema,
  senderUserId: UuidSchema,
  sequenceNumber: z.number().int().positive(),
  type: z.enum(['text', 'audio_call_event', 'system']),
  body: z.string().max(4000),
  /** Per-recipient read-at timestamps. Used for read receipts. */
  readBy: z.record(UuidSchema, IsoDateTimeSchema).default({}),
  createdAt: IsoDateTimeSchema,
});
export type Message = z.infer<typeof MessageSchema>;

export const SendMessageRequestSchema = z.object({
  chatId: UuidSchema,
  body: z.string().trim().min(1).max(4000),
  /** Client-generated ULID for safe retries; server treats duplicates as the same message. */
  clientMessageId: IdempotencyKeySchema,
});
export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;

/** Server → client realtime message event. */
export const MessageEventSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('message'), message: MessageSchema }),
  z.object({
    kind: z.literal('read_receipt'),
    chatId: UuidSchema,
    readerUserId: UuidSchema,
    upToSequence: z.number().int().positive(),
    readAt: IsoDateTimeSchema,
  }),
  z.object({
    kind: z.literal('typing'),
    chatId: UuidSchema,
    userId: UuidSchema,
    /** Typing state; debounced to ≤1/s server-side. */
    isTyping: z.boolean(),
  }),
  z.object({
    kind: z.literal('off_platform_warning'),
    chatId: UuidSchema,
    detectedPatterns: z.array(z.string()),
  }),
]);
export type MessageEvent = z.infer<typeof MessageEventSchema>;
